import { useState } from 'react';
import { AlertCircle, Loader2, MailCheck } from 'lucide-react';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import { Button } from '@/components/ui/button';
import { idpVerifyEmailUrl, openIdpForgotPassword } from '@/lib/idp';

export interface PendingCompanyOption {
  companyId: string;
  companyName: string;
}

interface PendingMembershipScreenProps {
  /** The companies the user has been offered but cannot use yet. */
  companies: ReadonlyArray<PendingCompanyOption>;
  /** The address the user signed in with; prefilled on the confirmation page. */
  email: string | null;
  /** Activation made at least one company usable: reload the organisations. */
  onActivated: () => void;
  /** The user would rather create their own company than wait. */
  onCreateOwn: () => void;
}

/**
 * Shown to someone who has been added to a company whose RsConnect account email is not
 * confirmed yet (#3702). They hold no access until they confirm the address, so instead of
 * dropping them into "Opret din virksomhed" (which would leave a stray company behind) this
 * explains what is needed. Never a dead end: they can always create their own company instead.
 */
export function PendingMembershipScreen({ companies, email, onActivated, onCreateOwn }: PendingMembershipScreenProps) {
  const api = useMesterplanApi();
  const [checking, setChecking] = useState(false);
  const [notYet, setNotYet] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const names = companies.map((c) => c.companyName);
  const heading =
    names.length === 1
      ? `Bekræft din email for at få adgang til ${names[0]}`
      : 'Bekræft din email for at få adgang til dine virksomheder';

  async function handleConfirmed() {
    setError(null);
    setNotYet(false);
    setChecking(true);
    try {
      const res = await api.me.activatePendingMemberships();
      if (!res.ok) {
        setError('Kunne ikke tjekke din email lige nu. Prøv igen om lidt.');
        return;
      }
      if (res.data.length < companies.length) {
        onActivated();
        return;
      }
      setNotYet(true);
    } catch {
      setError('Kunne ikke tjekke din email lige nu. Prøv igen om lidt.');
    } finally {
      setChecking(false);
    }
  }

  function handleConfirmEmail() {
    window.location.assign(idpVerifyEmailUrl(email));
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-light px-6 py-12 dark:bg-dark">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 shadow-lg dark:border-dark-border dark:bg-dark-card">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <MailCheck className="h-6 w-6" />
        </div>
        <h1 className="text-22 font-semibold text-dark dark:text-dark-text">{heading}</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted dark:text-dark-muted">
          Du er inviteret{names.length === 1 ? '' : ' til flere virksomheder'}, men din email er endnu ikke bekræftet.
          Så snart den er det, har du adgang. Vi har sendt en mail med et link til at bekræfte.
        </p>
        {names.length > 1 && (
          <ul className="mt-3 list-disc pl-5 text-[14px] text-dark dark:text-dark-text">
            {names.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        )}

        <div className="mt-6 space-y-3">
          <Button type="button" className="w-full" onClick={handleConfirmEmail}>
            Bekræft email
          </Button>
          <Button type="button" variant="secondary" className="w-full gap-2" disabled={checking} onClick={handleConfirmed}>
            {checking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Tjekker…
              </>
            ) : (
              'Jeg har bekræftet'
            )}
          </Button>
        </div>

        {notYet && (
          <p className="mt-3 flex items-start gap-1.5 text-[13px] text-warning" role="status">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            Vi kan endnu ikke se, at emailen er bekræftet. Bekræft den via linket, og prøv igen.
          </p>
        )}
        {error && (
          <p className="mt-3 flex items-start gap-1.5 text-[13px] text-danger" role="alert">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </p>
        )}

        <p className="mt-6 text-[13px] text-muted dark:text-dark-muted">
          Kender du ikke adgangskoden?{' '}
          <button type="button" className="text-primary underline" onClick={() => openIdpForgotPassword()}>
            Glemt adgangskode
          </button>
        </p>
        <p className="mt-2 text-[13px] text-muted dark:text-dark-muted">
          <button type="button" className="text-primary underline" onClick={onCreateOwn}>
            Opret en ny virksomhed i stedet
          </button>
        </p>
      </div>
    </div>
  );
}
